import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Loader2 } from 'lucide-react';
import api from '../api/client';
import DocumentViewer from './DocumentViewer';

interface DocumentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentUrl: string;
  documentName: string;
  documentType?: 'simple' | 'generated' | 'signed';
  documentId?: number;
}

const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  isOpen,
  onClose,
  documentUrl,
  documentName,
  documentType = 'simple',
  documentId,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadPreview();
    }

    return () => {
      // Nettoyer l'URL blob si nécessaire
      if (previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [isOpen, documentUrl]);

  const loadPreview = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Déterminer l'URL de preview selon le type
      let url = '';

      if (documentType === 'simple') {
        // Templates simples : utiliser l'endpoint preview
        // documentUrl contient déjà le chemin complet depuis handlePreviewDocument
        url = documentUrl;
      } else if (documentType === 'generated' && documentId) {
        // Documents générés : télécharger et créer blob
        url = `contracts/documents/${documentId}/download/`;
      } else if (documentType === 'signed' && documentId) {
        // Documents signés : télécharger et créer blob
        url = `contracts/documents/${documentId}/download_signed/`;
      }

      const response = await api.get(url, {
        responseType: 'blob',
      });

      const blob = response.data;
      const blobUrl = URL.createObjectURL(blob);

      setPreviewUrl(blobUrl);
      setIsLoading(false);
    } catch (err: any) {
      console.error('Erreur chargement preview:', err);
      setError(err.response?.data?.error || 'Impossible de charger l\'aperçu');
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  // Si on a une URL, utiliser le viewer universel
  if (!isLoading && !error && previewUrl) {
    return <DocumentViewer fileUrl={previewUrl} fileName={documentName} onClose={onClose} />;
  }

  // Fallback: ancien modal de chargement/erreur
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-6xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-gray-900">
                Aperçu : {documentName}
              </h3>
            </div>

            <div className="flex items-center gap-2">
              {/* Bouton Fermer */}
              <button
                onClick={onClose}
                className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Fermer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="relative" style={{ height: '80vh' }}>
            {isLoading && (
              <div className="flex flex-col items-center justify-center h-full">
                <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
                <p className="text-gray-600">Chargement de l'aperçu...</p>
              </div>
            )}

            {error && (
              <div className="flex flex-col items-center justify-center h-full p-6">
                <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
                <p className="text-red-600 font-medium mb-2">Erreur</p>
                <p className="text-gray-600 text-center">{error}</p>
                <button
                  onClick={loadPreview}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Réessayer
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentPreviewModal;
